import config from 'config'

import dayjs from 'utils/dayjs'
import numeric from './numeric'
import convertUnits from './convertUnits'

export default function format(value, type, convert = false, withUnits = true) {
  let result
  let units

  const unitFormat = config.user?.measurement_preference || 'meters'

  const imperial = unitFormat == 'feet'
  value = convert ? convertUnits(value, type, unitFormat) : value

  let duration

  if (['duration', 'longduration', 'digital_duration'].includes(type)) {
    duration = dayjs.duration(value, 'seconds')
  }

  switch (type) {
    case 'distance':
      result = value > 1000 ? numeric(value, '0,0') : numeric(value, '0,0.0')
      units = imperial ? 'mi' : 'km'
      break
    case 'longdistance':
      result = numeric(value, '0,0')
      units = imperial ? 'mi' : 'km'
      break
    case 'speed':
      result = numeric(value, '0,0.0')
      units = imperial ? 'mi/h' : 'km/h'
      break
    case 'swimming_pace':
      if (value && value > 0) {
        // `value` is a speed already formatted server-side based on user units:
        // - metric: km/h  -> pace is /100m
        // - imperial: mi/h -> pace is /100yd
        const secondsPerHour = (1 / value) * 60 * 60
        const secondsPer100 = imperial ? secondsPerHour / 17.6 : secondsPerHour / 10
        result = dayjs.utc(dayjs.duration(secondsPer100, 'seconds').asMilliseconds()).format('m:ss')
      } else {
        result = '0:00'
      }
      units = imperial ? '/100yd' : '/100m'
      break
    case 'swim_distance_meters':
      // Input is meters; output is meters (metric) or yards (imperial).
      if (!value || value === 0) {
        result = 0
        units = imperial ? 'yd' : 'm'
        break
      }
      if (imperial) {
        result = numeric(value * 1.0936133, '0,0')
        units = 'yd'
      } else {
        result = numeric(value, '0,0')
        units = 'm'
      }
      break
    case 'running_pace':
      if (value && value > 0) {
        // `value` is a speed already formatted server-side based on user units:
        // - metric: km/h
        // - imperial: mi/h
        // So the derived pace here is min/km or min/mi respectively.
        const secondsPerUnit = Math.round((1 / value) * 60 * 60)
        result = dayjs.utc(dayjs.duration(secondsPerUnit, 'seconds').asMilliseconds()).format('m:ss')
      } else {
        result = '0:00'
      }
      units = imperial ? '/mi' : '/km'
      break
    case 'pace_seconds':
      // Value is stored as seconds per kilometer (e.g., GAP stored as seconds/km).
      // Convert to seconds/mi for imperial users.
      if (value && value > 0) {
        const kmPerMile = 1.609344
        const totalSeconds = Math.round(parseFloat(value) * (imperial ? kmPerMile : 1))
        const mins = Math.floor(totalSeconds / 60)
        const secs = totalSeconds % 60
        result = `${mins}:${secs.toString().padStart(2, '0')}`
      } else {
        result = '0:00'
      }
      units = imperial ? '/mi' : '/km'
      break
    case 'pace_seconds_per_unit':
      // Value is seconds per unit already, where "unit" matches the user's preference:
      // - metric: seconds/km
      // - imperial: seconds/mi
      if (value && value > 0) {
        const totalSeconds = Math.round(parseFloat(value))
        const mins = Math.floor(totalSeconds / 60)
        const secs = totalSeconds % 60
        result = `${mins}:${secs.toString().padStart(2, '0')}`
      } else {
        result = '0:00'
      }
      units = imperial ? '/mi' : '/km'
      break
    case 'elevation':
      result = numeric(value, '0,0')
      units = imperial ? 'ft' : 'm'
      break
    case 'percent':
      result = numeric(100 * value, '0,0')
      units = '%'
      break
    case 'is_percent':
      result = numeric(value, '0,0')
      units = '%'
      break
    case 'grade':
      result = numeric(value, '0,0.0')
      units = '%'
      break
    case 'duration':
      if (duration.asDays() >= 1) {
        result = duration.days() + 'd ' + dayjs.utc(duration.asMilliseconds()).format('H[h] m[m]')
      } else if (duration.asHours() >= 1) {
        result = dayjs.utc(duration.asMilliseconds()).format('H[h] m[m]')
      } else {
        result = dayjs.utc(duration.asMilliseconds()).format('m[m] s[s]')
      }
      break
    case 'digital_duration':
      result = dayjs.utc(duration.asMilliseconds()).format('HH:mm:ss')
      break
    case 'longduration':
      if (duration.asDays() >= 1) {
        result = duration.days() + 'd ' + dayjs.utc(duration.asMilliseconds()).format('H[h] m[m]')
      } else if (duration.asHours() >= 1) {
        result = dayjs.utc(duration.asMilliseconds()).format('H[h] m[m]')
      } else {
        result = dayjs.utc(duration.asMilliseconds()).format('m[m]')
      }
      break
    case 'power':
      result = numeric(value, '0,0')
      units = 'W'
      break
    case 'energy':
      result = numeric(value, value >= 100 ? '0,0' : '0,0.0')
      units = 'kJ'
      break
    case 'power_per_kg':
      result = numeric(value, '0,0.00')
      units = 'W/kg'
      break
    case 'decimal':
      result = numeric(value, '0,0.00')
      break
    case 'short_decimal':
      result = numeric(value, '0,0.0')
      break
    case 'tss':
      result = numeric(value, '0,0')
      units = 'TSS'
      break
    case 'weight':
      result = numeric(value, '0.0')
      units = imperial ? 'lb' : 'kg'
      break
    case 'cadence':
      result = numeric(value, '0.0')
      units = 'rpm'
      break
    case 'heartrate':
      result = numeric(value, '0,0')
      units = 'bpm'
      break
    default:
      result = numeric(value, '0,0')
  }

  return [result, withUnits ? units : null].filter(Boolean).join(' ')
}
